import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ArtifactForm } from "../../components/ArtifactTable";
import { MODULES } from "../../config/modules";
import { Plus, Pencil, Trash2, Search } from "lucide-react";

const TYPE = "action_decision_log";

export default function ActionsDecisionsPage() {
  const { projectId } = useParams();
  const config = MODULES[TYPE];
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("actions");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/${TYPE}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const want = tab === "actions" ? "A" : "D";
    const q = query.trim().toLowerCase();
    return items
      .filter((i) => (i.type || "A") === want)
      .filter((i) => !q || [i.actionOrDecision, i.owner, i.status].some((v) => String(v || "").toLowerCase().includes(q)))
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  }, [items, tab, query]);

  const startNew = () => { setEditing(null); setForm({ type: tab === "actions" ? "A" : "D", status: "Open" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/projects/${projectId}/artifacts/${TYPE}/${editing.id}`, form);
      else await api.post(`/projects/${projectId}/artifacts/${TYPE}`, form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (item) => {
    try { await api.delete(`/projects/${projectId}/artifacts/${TYPE}/${item.id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const cols = tab === "actions"
    ? [["date", "Date"], ["actionOrDecision", "Action"], ["owner", "Owner"], ["dueDate", "Due"], ["status", "Status"]]
    : [["date", "Date"], ["actionOrDecision", "Decision"], ["owner", "Decided by"], ["status", "Status"]];

  return (
    <div data-testid="actions-decisions-page">
      <PageHeader overline="Delivery · Governance" title="Actions & Decisions"
        actions={<Button data-testid="add-action-decision-btn" className="rounded-none" onClick={startNew}>
          <Plus className="h-4 w-4 mr-1" /> Add {tab === "actions" ? "Action" : "Decision"}</Button>} />

      <div className="flex items-center gap-3 mt-2 mb-3">
        <div className="inline-flex border border-border" role="group">
          <button data-testid="tab-actions" onClick={() => setTab("actions")}
            className={`px-4 py-1.5 text-sm font-semibold ${tab === "actions" ? "bg-[#1E6FE8] text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Actions ({items.filter((i) => (i.type || "A") === "A").length})</button>
          <button data-testid="tab-decisions" onClick={() => setTab("decisions")}
            className={`px-4 py-1.5 text-sm font-semibold ${tab === "decisions" ? "bg-[#1E6FE8] text-white" : "text-muted-foreground hover:text-foreground"}`}>
            Decision Log ({items.filter((i) => i.type === "D").length})</button>
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="ad-search-input" className="pl-8 h-9" placeholder={`Search ${tab}…`} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>

      <div className="border border-border bg-card overflow-auto max-h-[calc(100vh-260px)]">
        <table className="w-full data-table text-sm">
          <thead><tr>{cols.map(([k, l]) => <th key={k}>{l}</th>)}<th className="w-16" /></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={cols.length + 1} className="text-center text-muted-foreground py-8">
              {query ? "No matches." : tab === "decisions" ? "No decisions recorded yet — a clean decision log strengthens governance." : "No actions yet."}</td></tr>}
            {filtered.map((item) => (
              <tr key={item.id} data-testid={`row-${tab === "actions" ? "action" : "decision"}`}>
                {cols.map(([k]) => (
                  <td key={k} className={k === "actionOrDecision" ? "max-w-md" : "whitespace-nowrap"}>
                    {["status"].includes(k) ? <StatusBadge value={item[k]} /> : String(item[k] ?? "—")}
                  </td>
                ))}
                <td className="whitespace-nowrap">
                  <button data-testid="edit-ad-btn" onClick={() => { setEditing(item); setForm(item); setOpen(true); }} className="p-1 hover:text-[#1E6FE8]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid="delete-ad-btn" onClick={() => remove(item)} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit" : "Add"} — {tab === "actions" ? "Action" : "Decision"}</DialogTitle></DialogHeader>
          <ArtifactForm fields={config.fields} value={form} onChange={setForm} />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-ad-btn" className="rounded-none" onClick={save}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
